import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import Loader from 'components/loader/loader.component';
import Select from 'components/select/select.component';
import Icon from 'components/icon/icon.component';
import { IKpuState, IObjectState, ITankState } from 'interfaces/applicant.interface';
import { getKpuState } from 'requests/history.request';
import { readingAgeMinutes } from 'utils/monitoring.utils';
import TankCard from './tank-card.component';
import './monitoring.style.scss';

const REFRESH_MS = 15_000;
const STALE_AFTER_MINUTES = 10;

type TankEntry = { tank: ITankState; kpuName?: string };

const MonitoringPage = () => {
  const { t } = useTranslation();

  const [objects, setObjects] = useState<IObjectState[]>([]);
  const [objectId, setObjectId] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const shellRef = useRef<HTMLDivElement>(null);
  // Kept in a ref so the polling effect never has to re-subscribe when it changes
  const selectedRef = useRef<number | undefined>(undefined);
  selectedRef.current = objectId;

  const fetchState = useCallback(() => {
    return getKpuState()
      .then((res) => {
        const next = res.objects ?? [];
        setObjects(next);
        setUpdatedAt(new Date());

        // Keep the operator's choice across refreshes; only pick a default when the
        // current one has gone away.
        const stillThere = next.some((o) => o.id === selectedRef.current);
        if (!stillThere) {
          const withTanks = next.find((o) => (o.kpus ?? []).some((k) => (k.tanks ?? []).length));
          setObjectId((withTanks ?? next[0])?.id);
        }
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void fetchState();
    const interval = setInterval(() => void fetchState(), REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchState]);

  // The browser can leave fullscreen without going through our button (Esc, F11)
  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      // Fullscreen can be refused (permissions, unsupported). Fall back to the
      // in-page expanded layout so the button still does something visible.
      setIsFullscreen((prev) => !prev);
    }
  };

  const selectableObjects = useMemo(
    () =>
      objects
        .filter((o) => (o.kpus ?? []).some((k) => (k.tanks ?? []).length))
        .map((o) => ({ id: String(o.id), name: o.objectName || `#${o.id}` })),
    [objects],
  );

  const tanks = useMemo<TankEntry[]>(() => {
    const scoped = objectId ? objects.filter((o) => o.id === objectId) : objects;
    return scoped
      .flatMap((o) => o.kpus ?? [])
      .flatMap((kpu: IKpuState) =>
        (kpu.tanks ?? []).map((tank) => ({ tank, kpuName: kpu.name })),
      );
  }, [objects, objectId]);

  const staleCount = useMemo(
    () =>
      tanks.filter(({ tank }) => {
        const age = readingAgeMinutes(tank.dateTime);
        return age === null || age > STALE_AFTER_MINUTES;
      }).length,
    [tanks],
  );

  return (
    <Loader loading={loading}>
      <div
        ref={shellRef}
        className={clsx('monitoring', isFullscreen && 'monitoring--fullscreen')}
      >
        <div className="wrapper monitoring__wrapper">
          <div className="monitoring__header">
            <div>
              <h1 className="title monitoring__title">{t('monitoring.title')}</h1>
              <div className="monitoring__status">
                <span
                  className={clsx(
                    'monitoring__dot',
                    staleCount > 0 ? 'monitoring__dot--warn' : 'monitoring__dot--ok',
                  )}
                />
                {staleCount > 0
                  ? t('monitoring.someStale', { count: staleCount, total: tanks.length })
                  : t('monitoring.allLive', { count: tanks.length })}
                {updatedAt && (
                  <span className="monitoring__updated">
                    {t('monitoring.updatedAt', {
                      time: updatedAt.toLocaleTimeString(),
                    })}
                  </span>
                )}
              </div>
            </div>

            <div className="monitoring__controls">
              {selectableObjects.length > 1 && (
                <div className="monitoring__objectSelect">
                  <Select
                    name="objectId"
                    hideNullOption
                    value={objectId !== undefined ? String(objectId) : ''}
                    options={selectableObjects}
                    onChange={(e) => setObjectId(Number(e.target.value))}
                  />
                </div>
              )}
              <button
                type="button"
                className="monitoring__fsBtn"
                onClick={toggleFullscreen}
                title={String(t(isFullscreen ? 'monitoring.exitFullscreen' : 'monitoring.fullscreen'))}
              >
                <Icon name={isFullscreen ? 'Minimize' : 'Maximize'} size={16} />
                <span>
                  {t(isFullscreen ? 'monitoring.exitFullscreen' : 'monitoring.fullscreen')}
                </span>
              </button>
            </div>
          </div>

          {tanks.length === 0 ? (
            <p className="monitoring__empty">{t('monitoring.noTanks')}</p>
          ) : (
            <div className="monitoring__grid">
              {tanks.map(({ tank, kpuName }) => (
                <TankCard
                  key={`tank_${tank.id}`}
                  tank={tank}
                  kpuName={kpuName}
                  staleAfterMinutes={STALE_AFTER_MINUTES}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Loader>
  );
};

export default MonitoringPage;
